import { useEffect, useState } from "react";
import { Mail, Send, Play, CheckCircle2, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const INCLUDED = [
  "All three walkthrough videos (Getting started · Going further · Crew dispatch & PM toolkit)",
  "Full feature rundown — jobs, timesheets, back-costing, safety, QR sign-ins, fleet, dispatch, invoicing, Xero, offline",
  "\u201CGet started with Fieldlink\u201D call-to-action linking to fieldlink.co.nz",
];

export default function OnboardingPanel() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = () => api.get("/admin/onboarding-emails").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/onboarding-email", { email: email.trim(), name: name.trim() || null });
      toast.success(`Onboarding pack sent to ${email.trim()}`);
      setEmail(""); setName("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px] items-start" data-testid="onboarding-panel">
      <div className="ops-card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-4 h-4 text-[#FF5F15]" />
          <span className="font-bold text-[15px]">Send onboarding pack</span>
        </div>
        <p className="text-[12.5px] text-zinc-500 dark:text-zinc-400 mb-5">
          Emails a prospective client the full Fieldlink pitch — walkthrough videos, feature rundown and a get-started link.
        </p>
        <form onSubmit={send} className="space-y-3 max-w-md">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Email *</label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="prospect@company.co.nz" data-testid="onboarding-email-input" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">First name (optional)</label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Dave — makes the greeting personal" data-testid="onboarding-name-input" />
          </div>
          <Button type="submit" disabled={busy || !email.trim()} className="bg-[#FF5F15] hover:bg-[#E64A00] text-white"
                  data-testid="onboarding-send-btn">
            <Send className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Send onboarding email"}
          </Button>
        </form>

        <div className="mt-6 pt-5 border-t border-zinc-200 dark:border-white/[0.08]">
          <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2.5">What's in the email</div>
          <ul className="space-y-2">
            {INCLUDED.map((x) => (
              <li key={x} className="flex items-start gap-2 text-[12.5px] text-zinc-600 dark:text-zinc-300">
                <Play className="w-3.5 h-3.5 text-[#FF5F15] mt-0.5 shrink-0" fill="currentColor" />
                {x}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="ops-card p-5" data-testid="onboarding-history">
        <div className="font-bold text-[15px] mb-3">Sent ({history.length})</div>
        {history.length === 0 ? (
          <div className="text-[12.5px] text-zinc-500">Nothing sent yet.</div>
        ) : (
          <div className="space-y-2.5 max-h-[420px] overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} className="flex items-start gap-2.5 text-[12.5px]" data-testid={`onboarding-sent-${h.id}`}>
                {h.status === "sent"
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                <div className="min-w-0">
                  <div className="font-semibold truncate">{h.email}{h.name ? ` · ${h.name}` : ""}</div>
                  <div className="text-[11px] text-zinc-500">
                    {new Date(h.created_at).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    {" · "}{h.sent_by}{h.error ? ` · ${h.error}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
